/** The score lines, the messages and Supernova's gauges (docs/spec/04 §2). */

import type { GameState } from '../sim';
import { chainMultiplier, FIELD, NOVA } from '../sim';
import type { Colour } from './font';
import { CYAN, DARK_GREY, GREY, MAGENTA, ORANGE, RED, SKY, WHITE, YELLOW } from './palette';
import type { Screen } from './screen';
import type { SpriteId } from './sprites';
import { badgesFor, CAPSULE_LOOK, drawSprite } from './sprites';

export interface HudInfo {
  readonly hiScore: number;
  /** The attract demo: say so, and invite a key. */
  readonly demo: boolean;
  /** Blinking things hold still (Reduce flashing). */
  readonly calm: boolean;
  /** The results are up: they say GAME OVER themselves. */
  readonly results: boolean;
}

const MID = FIELD.w / 2;
const MESSAGE_ROW = 17;

function points(n: number): string {
  return String(n).padStart(2, '0');
}

function drawScores(screen: Screen, s: GameState, info: HudInfo): void {
  const blink = info.calm || s.phase === 'gameover' || Math.floor(s.tick / 16) % 2 === 0;
  // One pixel down from the very top, so the letters are whole on a curved screen.
  if (blink) screen.text('1UP', 24, 1, RED);
  screen.text(points(s.score), 56, 9, WHITE, 'right');
  screen.text('HIGH SCORE', MID, 1, RED, 'center');
  screen.text(points(Math.max(info.hiScore, s.score)), MID + 24, 9, WHITE, 'right');
}

function drawReserve(screen: Screen, s: GameState): void {
  for (let i = 0; i < Math.min(s.lives, 6); i++) drawSprite(screen, 'lancer', 0, 10 + i * 17, 279);
  const badges = badgesFor(s.stage);
  let x = FIELD.w - 2 - badges.length * 8;
  for (const b of badges) {
    drawSprite(screen, `badge${b}` as SpriteId, 0, x + 4, 280);
    x += 8;
  }
}

const MULT_COLOURS: readonly Colour[] = [GREY, WHITE, YELLOW, ORANGE, RED, MAGENTA];

/** Supernova: the Nova stock, the chain and its clock, the power-ups running. */
function drawNova(screen: Screen, s: GameState): void {
  for (let i = 0; i < s.powers.novas; i++) screen.text('◆', 8 + i * 9, 18, CYAN);
  if (s.chain > 0) {
    const mult = chainMultiplier(s.chain);
    const colour = MULT_COLOURS[mult] ?? WHITE;
    screen.text(`x${mult}`, FIELD.w - 8, 18, colour, 'right');
    screen.text(String(s.chain), FIELD.w - 28, 18, GREY, 'right');
    const w = Math.round((32 * s.chainClock) / NOVA.chainClock);
    screen.fill(FIELD.w - 8 - 32, 27, 32, 1, DARK_GREY);
    screen.fill(FIELD.w - 8 - w, 27, w, 1, colour);
  }
  let x = 8;
  const gauge = (letter: string, colour: number, left: number, total: number): void => {
    screen.text(letter, x, 264, colour);
    if (total > 0) {
      screen.fill(x + 9, 270, 14, 1, DARK_GREY);
      screen.fill(x + 9, 270, Math.ceil((14 * left) / total), 1, colour);
      // Nearly out: the bar blinks.
      if (left < 120 && Math.floor(left / 8) % 2 === 0) screen.fill(x + 9, 267, 14, 1, colour);
    }
    x += total > 0 ? 28 : 12;
  };
  const pw = s.powers;
  if (pw.rapid > 0) gauge(CAPSULE_LOOK.rapid.letter, CAPSULE_LOOK.rapid.colour, pw.rapid, NOVA.timed);
  if (pw.spread > 0) gauge(CAPSULE_LOOK.spread.letter, CAPSULE_LOOK.spread.colour, pw.spread, NOVA.timed);
  if (pw.pierce > 0) gauge(CAPSULE_LOOK.pierce.letter, CAPSULE_LOOK.pierce.colour, pw.pierce, NOVA.timed);
  if (pw.chrono > 0) gauge(CAPSULE_LOOK.chrono.letter, CAPSULE_LOOK.chrono.colour, pw.chrono, NOVA.chronoTicks);
  if (pw.shield) gauge(CAPSULE_LOOK.shield.letter, CAPSULE_LOOK.shield.colour, 0, 0);
}

function drawEmpressBar(screen: Screen, s: GameState): void {
  const em = s.empress;
  if (!em || em.state === 'entering') return;
  const w = 120;
  const x = MID - w / 2;
  screen.box(x - 1, 29, w + 2, 5, GREY);
  screen.fill(x, 30, Math.round((w * em.hp) / em.maxHp), 3, em.phase === 2 ? RED : MAGENTA);
}

function centre(screen: Screen, text: string, row: number, colour: Colour): void {
  screen.shadowText(text, MID, row * 8, colour, 0, 'center');
}

function drawMessages(screen: Screen, s: GameState, info: HudInfo): void {
  const flash = info.calm || Math.floor(s.tick / 10) % 2 === 0;
  if (s.phase === 'gameover') {
    if (!info.results) centre(screen, 'GAME OVER', MESSAGE_ROW, RED);
    return;
  }
  if (s.capturedMsg > 0) {
    centre(screen, 'FIGHTER CAPTURED', MESSAGE_ROW, RED);
    return;
  }
  if (s.phase === 'intro') {
    if (s.stageKind === 'challenge') centre(screen, 'CHALLENGING STAGE', MESSAGE_ROW, CYAN);
    else if (s.stageKind === 'empress') {
      if (flash) centre(screen, 'WARNING', MESSAGE_ROW - 1, RED);
      centre(screen, 'THE EMPRESS APPROACHES', MESSAGE_ROW + 1, YELLOW);
    } else if (s.phaseT < 0) centre(screen, 'PLAYER 1', MESSAGE_ROW, CYAN);
    else centre(screen, `STAGE ${s.stage}`, MESSAGE_ROW, CYAN);
    return;
  }
  const c = s.challenge;
  if (s.phase === 'tally' && c) {
    if (c.perfect) {
      if (flash) centre(screen, 'PERFECT!', MESSAGE_ROW - 2, RED);
      if (s.phaseT > 60) centre(screen, `SPECIAL BONUS ${c.bonus} PTS`, MESSAGE_ROW + 1, YELLOW);
    } else {
      centre(screen, `NUMBER OF HITS ${String(c.hits).padStart(3, ' ')}`, MESSAGE_ROW - 2, CYAN);
      if (s.phaseT > 60) centre(screen, `BONUS ${String(c.bonus).padStart(6, ' ')}`, MESSAGE_ROW + 1, YELLOW);
    }
    return;
  }
  if (s.player.status === 'arriving') centre(screen, 'READY', MESSAGE_ROW, RED);
}

export function drawHud(screen: Screen, s: GameState, info: HudInfo): void {
  drawScores(screen, s, info);
  drawReserve(screen, s);
  if (s.mode === 'supernova') drawNova(screen, s);
  drawEmpressBar(screen, s);
  drawMessages(screen, s, info);
  if (info.demo) {
    centre(screen, 'DEMO', 4, SKY);
    if (info.calm || Math.floor(s.tick / 30) % 2 === 0) centre(screen, 'PRESS ANY KEY', 31, WHITE);
  }
}

/**
 * While Chrono runs, a frame of its own colour round the field, closing in from the corners as the
 * time runs out: the hive is slow on purpose, and the player should never take it for lag.
 */
export function drawChronoFrame(screen: Screen, left: number, tick: number): void {
  const part = Math.max(0, Math.min(1, left / NOVA.chronoTicks));
  // Nearly out: it blinks.
  if (left < 90 && Math.floor(tick / 6) % 2 === 0) return;
  const w = Math.round((FIELD.w / 2) * part);
  const h = Math.round(((FIELD.h - 32) / 2) * part);
  const top = 16;
  const bottom = FIELD.h - 17;
  for (const x of [0, FIELD.w - w]) {
    screen.fill(x, top, w, 1, MAGENTA);
    screen.fill(x, bottom, w, 1, MAGENTA);
  }
  for (const y of [top, bottom + 1 - h]) {
    screen.fill(0, y, 1, h, MAGENTA);
    screen.fill(FIELD.w - 1, y, 1, h, MAGENTA);
  }
}

/** The reckoning after a game (docs/spec/04 §2): shots, hits, the ratio; in Supernova the best chain. */
export function drawResults(screen: Screen, s: GameState): void {
  const { shots, hits, bestChain } = s.stats;
  const ratio = shots > 0 ? (hits / shots) * 100 : 0;
  const line = (label: string, value: string, row: number, colour: Colour): void => {
    screen.print(label, 4, row, colour);
    screen.text(value, FIELD.w - 32, row * 8, colour, 'right');
  };
  centre(screen, 'GAME OVER', 9, RED);
  centre(screen, '-RESULTS-', 13, RED);
  line('SHOTS FIRED', String(shots), 16, YELLOW);
  line('NUMBER OF HITS', String(hits), 19, YELLOW);
  line('HIT-MISS RATIO', `${ratio.toFixed(1)} %`, 22, WHITE);
  if (s.mode === 'supernova') line('BEST CHAIN', String(bestChain), 25, CYAN);
  line('STAGE REACHED', String(s.stage), s.mode === 'supernova' ? 28 : 25, WHITE);
}
